import React, { useEffect, useRef, useState } from "react";
import { Moon as MoonIcon, X, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";

const NOTES = [
  "Hi! I'm Luna 🌙 — here to help you",
  "Stuck on a question? Ask me!",
  "Need a quick explanation?",
  "Let's study together ✨",
];

const CHIPS = [
  "Explain this simply",
  "Give me a study tip",
  "Quiz me on a topic",
  "Summarize a concept",
];

function getSessionId() {
  let s = localStorage.getItem("quizer_luna_session");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("quizer_luna_session", s);
  }
  return s;
}

export default function Luna() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [noteIdx, setNoteIdx] = useState(0);
  const [noteHidden, setNoteHidden] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (noteHidden || open) return;
    const t = setInterval(() => setNoteIdx((i) => (i + 1) % NOTES.length), 4500);
    return () => clearInterval(t);
  }, [noteHidden, open]);

  useEffect(() => {
    if (!open) return;
    api.get(`/tutor/history?session_id=${sessionId.current}`).then(({ data }) => {
      setMessages(data.messages || []);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/tutor/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I had trouble answering just now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button + note */}
      {!open && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
          {!noteHidden && (
            <div className="glass-strong relative max-w-[230px] rounded-2xl rounded-br-sm px-4 py-3 text-sm text-zinc-200 shadow-xl" data-testid="luna-note">
              <button
                onClick={() => setNoteHidden(true)}
                className="absolute -right-2 -top-2 rounded-full bg-white/10 p-1 text-zinc-300 hover:text-white"
                data-testid="luna-note-dismiss"
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
              {NOTES[noteIdx]}
            </div>
          )}
          <button
            onClick={() => setOpen(true)}
            data-testid="luna-open-button"
            className="relative flex h-16 w-16 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur-xl transition-transform hover:scale-105"
          >
            <span className="pulse-ring absolute inset-0 rounded-full border border-white/40"
                  style={{ animation: "pulse-ring 2.4s ease-out infinite" }} />
            <MoonIcon className="h-7 w-7 text-white" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div className="glass-strong fixed bottom-6 right-6 z-50 flex h-[560px] max-h-[82vh] w-[380px] max-w-[92vw] flex-col overflow-hidden rounded-3xl shadow-2xl" data-testid="luna-panel">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/5">
                <MoonIcon className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-sans text-sm font-semibold">Luna</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">AI Study Tutor</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="luna-close-button" className="rounded-full p-1.5 text-zinc-400 hover:text-white" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
            {messages.length === 0 && (
              <div className="space-y-4">
                <p className="text-zinc-300">Hi, I'm Luna 🌙 — ask me anything about your studies and I'll explain it clearly.</p>
                <div className="flex flex-wrap gap-2">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid={`luna-chip-${c.toLowerCase().replace(/\s/g, "-")}`}
                      className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-white/30 hover:text-white"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                    m.role === "user"
                      ? "rounded-br-sm bg-white text-[#06080d]"
                      : "rounded-bl-sm border border-white/10 bg-white/5 text-zinc-200"
                  }`}
                  data-testid={`luna-msg-${m.role}`}
                >
                  {m.role === "user" ? m.content : <div className="space-y-1 leading-relaxed">{renderMarkdown(m.content)}</div>}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-zinc-500" data-testid="luna-thinking">
                <Sparkles className="h-4 w-4 animate-pulse" />
                <span className="font-mono text-xs">Luna is thinking…</span>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-white/10 px-4 py-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Luna…"
              data-testid="luna-input"
              className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="luna-send-button"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#06080d] transition-opacity disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
